import { useState, useEffect, useCallback } from 'react'
import req from '../api/request'
import { DEFAULT_PREFERENCES } from '../constants/notificationCategories'
import {
  CategoryPreference,
  NotificationPreferencesMap,
  NotificationChannelPreference,
  UseNotificationPreferencesReturn,
} from '../types/Notifications'

/**
 * Custom hook for managing notification preferences via API
 * Handles fetching, updating, and caching user notification preferences
 * Implements optimistic updates for better UX
 * @returns {UseNotificationPreferencesReturn} Preferences state and management functions
 */
const useNotificationPreferences = (): UseNotificationPreferencesReturn => {
  const [preferences, setPreferences] = useState<NotificationPreferencesMap>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [error, setError] = useState<string | null>(null)
  const [updatingCategory, setUpdatingCategory] = useState<string | null>(null)

  /**
   * Fetch all notification preferences from API
   * @returns {Promise<void>}
   */
  const fetchPreferences = useCallback(async () => {
    try {
      setIsLoading(true)
      setError(null)

      const response = await req('/users/me/preferences')

      // Convert array of preferences to object keyed by category
      const preferencesMap: NotificationPreferencesMap = {}
      if (Array.isArray(response)) {
        response.forEach((pref: CategoryPreference) => {
          preferencesMap[pref.category] = pref
        })
      }

      setPreferences(preferencesMap)
    } catch (err: any) {
      console.error('🚨 fetchPreferences: Error fetching preferences:', err)
      setError(err?.message || 'Failed to load notification preferences')
    } finally {
      setIsLoading(false)
    }
  }, [])

  /**
   * Update a specific category's notification preference
   * Implements optimistic update with rollback on failure
   * @param {string} category - Category to update (API enum value)
   * @param {Partial<NotificationChannelPreference>} settings - Preference settings to update
   * @returns {Promise<boolean>} Success status
   */
  const updatePreference = useCallback(
    async (
      category: string,
      settings: Partial<NotificationChannelPreference>
    ): Promise<boolean> => {
      // Store original value for rollback
      const originalPreference = preferences[category]

      // Optimistic update
      const optimisticPreference: CategoryPreference = {
        ...originalPreference,
        category,
        ...settings,
      } as CategoryPreference
      setPreferences((prev) => ({
        ...prev,
        [category]: optimisticPreference,
      }))

      setUpdatingCategory(category)
      setError(null)

      try {
        const response = await req(`/users/me/preferences/${category}`, {
          method: 'PUT',
          body: JSON.stringify(settings),
        })

        // Update with actual server response
        setPreferences((prev) => ({
          ...prev,
          [category]: response,
        }))

        return true
      } catch (err: any) {
        console.error('🚨 updatePreference: Error updating preference:', err)

        // Rollback to original value
        if (originalPreference) {
          setPreferences((prev) => ({
            ...prev,
            [category]: originalPreference,
          }))
        } else {
          // Remove if it didn't exist before
          setPreferences((prev) => {
            const newPrefs = { ...prev }
            delete newPrefs[category]
            return newPrefs
          })
        }

        setError(err?.message || 'Failed to update notification preference')
        return false
      } finally {
        setUpdatingCategory(null)
      }
    },
    [preferences]
  )

  /**
   * Get preference for a specific category
   * Returns default values if preference doesn't exist
   * @param {string} category - Category to get preference for
   * @returns {CategoryPreference} Preference object with enabled flags
   */
  const getPreference = useCallback(
    (category: string): CategoryPreference => {
      const pref = preferences[category]
      if (pref) {
        return pref
      }

      // Return default values if no preference exists
      return {
        category,
        ...DEFAULT_PREFERENCES,
      }
    },
    [preferences]
  )

  /**
   * Check if push notifications are enabled for a category
   * @param {string} category - Category to check
   * @returns {boolean} Whether push is enabled
   */
  const isPushEnabled = useCallback(
    (category: string): boolean => {
      const pref = getPreference(category)
      return pref.push_enabled !== false // Default to true
    },
    [getPreference]
  )

  /**
   * Toggle push notification for a category
   * @param {string} category - Category to toggle
   * @returns {Promise<boolean>} Success status
   */
  const togglePushNotification = useCallback(
    async (category: string): Promise<boolean> => {
      const currentValue = isPushEnabled(category)
      const newValue = !currentValue

      return await updatePreference(category, {
        push_enabled: newValue,
      })
    },
    [isPushEnabled, updatePreference]
  )

  /**
   * Get all channel preferences for a specific category
   * Returns all three channel states: push, email, sms
   * @param {string} category - Category to get preferences for
   * @returns {NotificationChannelPreference} Object with push_enabled, email_enabled, sms_enabled
   */
  const getChannelPreferences = useCallback(
    (category: string): NotificationChannelPreference => {
      const pref = getPreference(category)
      return {
        push_enabled: pref.push_enabled !== false,
        email_enabled: pref.email_enabled !== false,
        sms_enabled: pref.sms_enabled !== false,
      }
    },
    [getPreference]
  )

  /**
   * Check if a specific channel is enabled for a category
   * @param {string} category - Category to check
   * @param {string} channel - Channel to check (push, email, sms)
   * @returns {boolean} Whether the channel is enabled
   */
  const isChannelEnabled = useCallback(
    (category: string, channel: string): boolean => {
      const pref = getPreference(category)
      const channelKey = `${channel}_enabled` as keyof CategoryPreference
      return (pref[channelKey] as boolean) !== false // Default to true
    },
    [getPreference]
  )

  /**
   * Toggle a specific notification channel for a category
   * @param {string} category - Category to update
   * @param {string} channel - Channel to toggle (push, email, sms)
   * @param {boolean} newValue - New value for the channel
   * @returns {Promise<boolean>} Success status
   */
  const toggleChannel = useCallback(
    async (
      category: string,
      channel: string,
      newValue: boolean
    ): Promise<boolean> => {
      const channelKey = `${channel}_enabled`

      return await updatePreference(category, {
        [channelKey]: newValue,
      } as Partial<NotificationChannelPreference>)
    },
    [updatePreference]
  )

  /**
   * Refresh preferences from server
   * @returns {Promise<void>}
   */
  const refresh = useCallback(async (): Promise<void> => {
    await fetchPreferences()
  }, [fetchPreferences])

  // Fetch preferences on mount
  useEffect(() => {
    fetchPreferences()
  }, [fetchPreferences])

  return {
    preferences,
    isLoading,
    error,
    updatingCategory,
    getPreference,
    isPushEnabled,
    updatePreference,
    togglePushNotification,
    getChannelPreferences,
    isChannelEnabled,
    toggleChannel,
    refresh,
  }
}

export default useNotificationPreferences
